import React from 'react';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import ChunkCard from './ChunkCard';
import {GeneratedVideoChunk} from '../../../data/models/LibraryFile/GeneratedVideoChunk';
import {EditChunkOption} from '../../../data/repositories/LibraryRepository';
import styles from './DraggableChunks.module.css';

interface DraggableChunksProps {
  chunks: GeneratedVideoChunk[];
  selectedChunk?: GeneratedVideoChunk;
  onEditChunk: (option: EditChunkOption, chunkId: string) => void;
  onSelectChunk: (chunkId: string) => void;
  onChunksReorder: (chunks: GeneratedVideoChunk[]) => void;
  totalTimelineSeconds: number;
  pixelsPerSecond: number;
}

const SortableChunkCard: React.FC<{
  chunk: GeneratedVideoChunk;
  state: 'selected' | 'dimmed' | 'normal';
  onEditChunk: (option: EditChunkOption, chunkId: string) => void;
  onSelectChunk: (chunkId: string) => void;
  pixelsPerSecond: number;
}> = ({chunk, state, onEditChunk, onSelectChunk, pixelsPerSecond}) => {
  const {setNodeRef, attributes, listeners, transform, transition} =
    useSortable({id: chunk.id});

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    touchAction: 'none',
  };

  return (
    <div ref={setNodeRef} style={style}>
      <ChunkCard
        chunk={chunk}
        state={state}
        onEditChunk={onEditChunk}
        onSelectChunk={onSelectChunk}
        pixelsPerSecond={pixelsPerSecond}
        dragHandleProps={{...attributes, ...listeners}}
      />
    </div>
  );
};

export const DraggableChunks: React.FC<DraggableChunksProps> = ({
  chunks,
  selectedChunk,
  onEditChunk,
  onSelectChunk,
  onChunksReorder,
  totalTimelineSeconds,
  pixelsPerSecond,
}) => {
  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event: any) => {
    const {active, over} = event;
    if (!over || active.id === over.id) return;

    const oldIndex = chunks.findIndex(chunk => chunk.id === active.id);
    const newIndex = chunks.findIndex(chunk => chunk.id === over.id);

    const reordered = arrayMove(chunks, oldIndex, newIndex);
    onChunksReorder(reordered);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}>
      <SortableContext
        items={chunks.map(c => c.id)}
        strategy={horizontalListSortingStrategy}>
        <div
          className={styles.chunksListContainer}
          style={{
            width: `${totalTimelineSeconds * pixelsPerSecond}px`,
            display: 'flex',
          }}>
          {chunks.map(chunk => (
            <SortableChunkCard
              key={chunk.id}
              chunk={chunk}
              state={
                selectedChunk?.id === chunk.id
                  ? 'selected'
                  : selectedChunk
                  ? 'dimmed'
                  : 'normal'
              }
              onEditChunk={onEditChunk}
              onSelectChunk={onSelectChunk}
              pixelsPerSecond={pixelsPerSecond}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};
